import React, { useState, useEffect, useCallback } from 'react';
import { securityAPI } from '../../services/api';
import './SystemHealth.css';

type ServiceStatus = 'OK' | 'WARNING' | 'ERROR';

interface EmailFailure {
  id: number;
  timestamp: string;
  recipient: string;
  subject: string;
  error: string;
  retryCount: number;
}

interface HealthData {
  email: {
    status: ServiceStatus;
    failures24h: number;
    unacknowledged: number;
    recentFailures: EmailFailure[];
  };
  database: {
    status: ServiceStatus;
    lastBackupAt?: string;
  };
  mfa: {
    status: ServiceStatus;
    lastMfaLoginAt?: string;
  };
  authentication: {
    status: ServiceStatus;
    logins24h: number;
    lockedAccounts: number;
  };
}

const POLL_INTERVAL_MS = 60_000;

const statusClass = (s: ServiceStatus) => {
  if (s === 'OK')      return 'sh-status--ok';
  if (s === 'WARNING') return 'sh-status--warn';
  return 'sh-status--error';
};

const statusLabel = (s: ServiceStatus) => {
  if (s === 'OK')      return '● OK';
  if (s === 'WARNING') return '▲ WARNING';
  return '✕ ERROR';
};

const fmt = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '—';

const SystemHealth: React.FC = () => {
  const [health, setHealth]         = useState<HealthData | null>(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [acking, setAcking]         = useState(false);
  const [ackSuccess, setAckSuccess] = useState(false);
  const [ackError, setAckError]     = useState<string | null>(null);

  const fetchHealth = useCallback(() => {
    setError(null);
    securityAPI.getHealth()
      .then(res => {
        setHealth(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load system health data.');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchHealth();
    const timer = setInterval(fetchHealth, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchHealth]);

  const handleAcknowledge = async () => {
    setAcking(true);
    setAckError(null);
    setAckSuccess(false);
    try {
      await securityAPI.acknowledgeHealthIssue();
      setAckSuccess(true);
      fetchHealth();
    } catch {
      setAckError('Failed to acknowledge issue. Please try again.');
    } finally {
      setAcking(false);
    }
  };

  const emailIssue =
    health?.email?.status === 'WARNING' ||
    health?.email?.status === 'ERROR';

  return (
    <div className="sh">
      <div className="sh-header-row">
        <h3 className="sh-heading">System Health Monitor</h3>
        <button className="sh-refresh-btn" onClick={fetchHealth} disabled={loading}>
          {loading ? 'Refreshing…' : '↺ Refresh'}
        </button>
      </div>
      <p className="sh-poll-note">Auto-refreshes every 60 seconds.</p>

      {error && <p className="sh-error">{error}</p>}

      {/* Email issue banner */}
      {emailIssue && health && (
        <div className="sh-banner sh-banner--warn">
          <strong>Email service issue detected.</strong> MTA notifications may be affected.
          Review below and escalate to Matt Murphy.
        </div>
      )}

      {/* Status cards */}
      {health && (
        <div className="sh-cards-grid">
          {/* Email Service */}
          <div className={`sh-card ${statusClass(health.email.status)}`}>
            <div className="sh-card-title">Email Service (ACS)</div>
            <div className={`sh-card-status ${statusClass(health.email.status)}`}>
              {statusLabel(health.email.status)}
            </div>
            <div className="sh-card-detail">
              <span className="sh-card-detail-label">Failures (24h):</span>{' '}
              {health.email.failures24h}
            </div>
            <div className="sh-card-detail">
              <span className="sh-card-detail-label">Unacknowledged:</span>{' '}
              {health.email.unacknowledged}
            </div>
          </div>

          {/* Database */}
          <div className={`sh-card ${statusClass(health.database.status)}`}>
            <div className="sh-card-title">Database</div>
            <div className={`sh-card-status ${statusClass(health.database.status)}`}>
              {statusLabel(health.database.status)}
            </div>
            <div className="sh-card-detail">
              <span className="sh-card-detail-label">Last backup:</span>{' '}
              {fmt(health.database.lastBackupAt)}
            </div>
          </div>

          {/* MFA Service */}
          <div className={`sh-card ${statusClass(health.mfa.status)}`}>
            <div className="sh-card-title">MFA Service</div>
            <div className={`sh-card-status ${statusClass(health.mfa.status)}`}>
              {statusLabel(health.mfa.status)}
            </div>
            <div className="sh-card-detail">
              <span className="sh-card-detail-label">Last MFA login:</span>{' '}
              {fmt(health.mfa.lastMfaLoginAt)}
            </div>
          </div>

          {/* Authentication */}
          <div className={`sh-card ${statusClass(health.authentication.status)}`}>
            <div className="sh-card-title">Authentication</div>
            <div className={`sh-card-status ${statusClass(health.authentication.status)}`}>
              {statusLabel(health.authentication.status)}
            </div>
              <div className="sh-card-stat-row">
              <div className="sh-card-stat">
                <span className="sh-card-stat-val">{health.authentication.logins24h}</span>
                <span className="sh-card-stat-label">Logins (24h)</span>
              </div>
              <div className="sh-card-stat">
                <span className={`sh-card-stat-val${health.authentication.lockedAccounts > 0 ? ' sh-warn-val' : ''}`}>
                  {health.authentication.lockedAccounts}
                </span>
                <span className="sh-card-stat-label">Locked</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email failure log */}
      {health && (
        <div className="sh-failures-card">
          <div className="sh-failures-header">
            <h4 className="sh-failures-title">Recent Email Failures</h4>
            {emailIssue && (
              <div className="sh-ack-area">
                {ackError  && <span className="sh-ack-error">{ackError}</span>}
                {ackSuccess && <span className="sh-ack-success">Issue acknowledged.</span>}
                <button
                  className="sh-ack-btn"
                  onClick={handleAcknowledge}
                  disabled={acking}
                >
                  {acking ? 'Acknowledging…' : 'Acknowledge Issue'}
                </button>
              </div>
            )}
          </div>

          {(!health.email.recentFailures || health.email.recentFailures.length === 0) ? (
            <p className="sh-no-failures">No recent email failures on record.</p>
          ) : (
            <div className="sh-table-wrap">
              <table className="sh-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Recipient</th>
                    <th>Subject</th>
                    <th>Error</th>
                    <th>Retry Count</th>
                  </tr>
                </thead>
                <tbody>
                  {health.email.recentFailures.map(f => (
                    <tr key={f.id}>
                      <td className="sh-nowrap">{fmt(f.timestamp)}</td>
                      <td>{f.recipient}</td>
                      <td>{f.subject}</td>
                      <td className="sh-error-text">{f.error}</td>
                      <td className="sh-center">
                        <span className={f.retryCount > 2 ? 'sh-retry-high' : 'sh-retry-normal'}>
                          {f.retryCount}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {loading && !health && <p className="sh-loading">Loading system health…</p>}
    </div>
  );
};

export default SystemHealth;
